<script setup>
import { ref, computed, unref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert, useTrack } from 'dashboard/composables';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import contactFilterItems from 'dashboard/routes/dashboard/contacts/contactFilterItems';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { createCustomViewRequestKeys } from 'dashboard/helper/customViewRequestKey';
import countries from 'shared/constants/countries';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';

import ContactsHeader from 'dashboard/components-next/Contacts/ContactsHeader/ContactHeader.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import ContactExportDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactExportDialog.vue';
import ContactImportDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactImportDialog.vue';
import CreateSegmentDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateSegmentDialog.vue';
import DeleteSegmentDialog from 'dashboard/components-next/Contacts/ContactsForm/DeleteSegmentDialog.vue';
import ContactsFilter from 'dashboard/components-next/filter/ContactsFilter.vue';

const props = defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  segmentsId: { type: [String, Number], default: 0 },
  activeSegment: { type: Object, default: null },
  hasAppliedFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
  'importAccepted',
  'exportAccepted',
]);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const createNewContactDialogRef = ref(null);
const contactExportDialogRef = ref(null);
const contactImportDialogRef = ref(null);
const importError = ref('');
let importRevision = 0;
const contextRevision = ref(0);
const exportError = ref('');
const segmentError = ref('');
const segmentRequestKeys = createCustomViewRequestKeys();
const segmentDeleteError = ref('');
const segmentUpdateError = ref('');
const isUpdatingSegment = ref(false);
const createSegmentDialogRef = ref(null);
const deleteSegmentDialogRef = ref(null);

const currentContext = () => ({
  revision: contextRevision.value,
  accountId: String(route.params.accountId),
  fullPath: String(route.fullPath),
  segmentsId: String(props.segmentsId),
});
const isCurrentContext = context =>
  context.revision === contextRevision.value &&
  context.accountId === String(route.params.accountId) &&
  context.fullPath === String(route.fullPath) &&
  context.segmentsId === String(props.segmentsId);

const showFiltersModal = ref(false);
const appliedFilter = ref([]);

const appliedFilters = useMapGetter('contacts/getAppliedContactFiltersV4');
const savedContactFilters = useMapGetter('contacts/getAppliedContactFilters');
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const labels = useMapGetter('labels/getLabels');
const hasActiveSegments = computed(
  () => props.activeSegment && props.segmentsId !== 0
);
const activeSegmentName = computed(() => props.activeSegment?.name);

const openCreateNewContactDialog = () => {
  createNewContactDialogRef.value?.dialogRef.open();
};
const openContactImportDialog = () => {
  importError.value = '';
  contactImportDialogRef.value?.dialogRef.open();
};
const openContactExportDialog = () => {
  exportError.value = '';
  contactExportDialogRef.value?.dialogRef.open();
};
const openCreateSegmentDialog = () => {
  segmentError.value = '';
  createSegmentDialogRef.value?.dialogRef.open();
};
const openDeleteSegmentDialog = () => {
  segmentDeleteError.value = '';
  deleteSegmentDialogRef.value?.dialogRef.open();
};

const onCreate = async contact => {
  const context = currentContext();
  try {
    await store.dispatch('contacts/create', contact);
    if (!isCurrentContext(context)) return;
    createNewContactDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch (error) {
    if (!isCurrentContext(context)) return;
    const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
    let message = t(`${i18nPrefix}.ERROR_MESSAGE`);
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email'))
        message = t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`);
      else if (error.data.includes('phone_number'))
        message = t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`);
    } else if (error instanceof ExceptionWithMessage) {
      message = error.data;
    }
    createNewContactDialogRef.value?.onError(message);
    useAlert(message);
  }
};

const onImport = async file => {
  importRevision += 1;
  const revision = importRevision;
  const accountId = String(route.params.accountId);
  importError.value = '';
  try {
    await store.dispatch('contacts/import', file);
    if (
      revision !== importRevision ||
      accountId !== String(route.params.accountId)
    )
      return;
    contactImportDialogRef.value?.dialogRef.close();
    emit('importAccepted');
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SUCCESS_MESSAGE')
    );
    useTrack(CONTACTS_EVENTS.IMPORT_SUCCESS);
  } catch (error) {
    if (
      revision !== importRevision ||
      accountId !== String(route.params.accountId)
    )
      return;
    importError.value =
      error instanceof ExceptionWithMessage
        ? error.data
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.ERROR_MESSAGE');
    useAlert(importError.value);
    useTrack(CONTACTS_EVENTS.IMPORT_FAILURE);
  }
};

watch(
  [() => route.params.accountId, () => route.fullPath, () => props.segmentsId],
  () => {
    importRevision += 1;
    contextRevision.value += 1;
    isUpdatingSegment.value = false;
    showFiltersModal.value = false;
    segmentUpdateError.value = '';
    exportError.value = '';
    segmentError.value = '';
    segmentDeleteError.value = '';
  },
  { flush: 'sync' }
);
onBeforeUnmount(() => {
  importRevision += 1;
  contextRevision.value += 1;
  isUpdatingSegment.value = false;
});

const onExport = async query => {
  const context = currentContext();
  exportError.value = '';
  try {
    await store.dispatch('contacts/export', query);
    if (!isCurrentContext(context)) return;
    contactExportDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SUCCESS_MESSAGE')
    );
    emit('exportAccepted');
  } catch (error) {
    if (!isCurrentContext(context)) return;
    exportError.value =
      error.message ||
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.ERROR_MESSAGE');
    useAlert(exportError.value);
  }
};

const onCreateSegment = async payload => {
  const context = currentContext();
  segmentError.value = '';
  try {
    const payloadData = {
      ...payload,
      query: filterQueryGenerator(savedContactFilters.value),
    };
    const response = await store.dispatch(
      'customViews/create',
      segmentRequestKeys.withKey(payloadData)
    );
    segmentRequestKeys.confirm();
    if (!isCurrentContext(context)) return;
    createSegmentDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.SUCCESS_MESSAGE')
    );
    const segmentId = response?.data?.id;
    if (!segmentId) return;
    // Navigate to the created segment
    router.push({
      name: 'contacts_dashboard_segments_index',
      params: { segmentId },
      query: { ...route.query, page: 1 },
    });
  } catch {
    if (!isCurrentContext(context)) return;
    segmentError.value = t(
      'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.ERROR_MESSAGE'
    );
    useAlert(segmentError.value);
  }
};

const onDeleteSegment = async payload => {
  const context = currentContext();
  segmentDeleteError.value = '';
  try {
    await store.dispatch('customViews/delete', {
      id: Number(props.segmentsId),
      ...payload,
    });
    if (!isCurrentContext(context)) return;
    router.push({
      name: 'contacts_dashboard_index',
      query: {
        ...route.query,
        page: 1,
      },
    });
    deleteSegmentDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.SUCCESS_MESSAGE')
    );
  } catch (error) {
    if (!isCurrentContext(context)) return;
    segmentDeleteError.value = t(
      'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.ERROR_MESSAGE'
    );
    useAlert(segmentDeleteError.value);
  }
};

const closeAdvanceFiltersModal = () => {
  if (isUpdatingSegment.value) return;
  showFiltersModal.value = false;
  appliedFilter.value = [];
};

const clearFilters = async () => {
  emit('clearFilters');
};

const onApplyFilter = async payload => {
  payload = useSnakeCase(payload);
  emit('applyFilter', filterQueryGenerator(payload));
  showFiltersModal.value = false;
};

const onUpdateSegment = async (payload, segmentName) => {
  if (isUpdatingSegment.value) return;
  const context = currentContext();
  isUpdatingSegment.value = true;
  segmentUpdateError.value = '';
  try {
    await store.dispatch('customViews/update', {
      ...props.activeSegment,
      name: segmentName,
      query: filterQueryGenerator(useSnakeCase(payload)),
    });
    if (!isCurrentContext(context)) return;
    showFiltersModal.value = false;
    useAlert(t('CONTACTS_LAYOUT.FILTER.UPDATE_SUCCESS'));
  } catch {
    if (!isCurrentContext(context)) return;
    segmentUpdateError.value = t('CONTACTS_LAYOUT.FILTER.UPDATE_ERROR');
  } finally {
    if (isCurrentContext(context)) isUpdatingSegment.value = false;
  }
};

const setParamsForEditSegmentModal = () => {
  return {
    countries,
    filterTypes: contactFilterItems,
    allCustomAttributes: useSnakeCase(contactAttributes.value),
    labels: labels.value || [],
  };
};

const initializeSegmentToFilterModal = segment => {
  const query = unref(segment)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditSegmentModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
};

const onToggleFilters = () => {
  if (isUpdatingSegment.value) return;
  segmentUpdateError.value = '';
  appliedFilter.value = [];
  if (hasActiveSegments.value) {
    initializeSegmentToFilterModal(props.activeSegment);
  } else {
    appliedFilter.value = props.hasAppliedFilters
      ? [...appliedFilters.value]
      : [
          {
            attributeKey: 'name',
            filterOperator: 'equal_to',
            values: '',
            queryOperator: 'and',
            attributeModel: 'standard',
          },
        ];
  }
  showFiltersModal.value = true;
};

defineExpose({
  onToggleFilters,
});
</script>

<template>
  <ContactsHeader
    :show-search="showSearch"
    :search-value="searchValue"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :header-title="headerTitle"
    :is-segments-view="hasActiveSegments"
    :is-label-view="isLabelView"
    :is-active-view="isActiveView"
    :has-active-filters="hasAppliedFilters"
    :button-label="t('CONTACTS_LAYOUT.HEADER.MESSAGE_BUTTON')"
    @search="emit('search', $event)"
    @update:sort="emit('update:sort', $event)"
    @add="openCreateNewContactDialog"
    @import="openContactImportDialog"
    @export="openContactExportDialog"
    @filter="onToggleFilters"
    @create-segment="openCreateSegmentDialog"
    @delete-segment="openDeleteSegmentDialog"
  >
    <template #filter>
      <div
        class="fixed inset-x-3 top-24 z-40 sm:absolute sm:inset-x-auto sm:mt-1 sm:ltr:right-0 sm:rtl:left-0 sm:top-full"
      >
        <ContactsFilter
          v-if="showFiltersModal"
          v-model="appliedFilter"
          :segment-name="activeSegmentName"
          :saving="isUpdatingSegment"
          :error="segmentUpdateError"
          :is-segment-view="hasActiveSegments"
          @apply-filter="onApplyFilter"
          @update-segment="onUpdateSegment"
          @close="closeAdvanceFiltersModal"
          @clear-filters="clearFilters"
        />
      </div>
    </template>
  </ContactsHeader>

  <CreateNewContactDialog
    :key="`create-${contextRevision}`"
    ref="createNewContactDialogRef"
    @create="onCreate"
  />
  <ContactExportDialog
    :key="`export-${contextRevision}`"
    ref="contactExportDialogRef"
    :error="exportError"
    @export="onExport"
  />
  <ContactImportDialog
    :key="`import-${contextRevision}`"
    ref="contactImportDialogRef"
    :error="importError"
    @import="onImport"
  />
  <CreateSegmentDialog
    :key="`segment-create-${contextRevision}`"
    ref="createSegmentDialogRef"
    :error="segmentError"
    @create="onCreateSegment"
  />
  <DeleteSegmentDialog
    :key="`segment-delete-${contextRevision}`"
    ref="deleteSegmentDialogRef"
    :error="segmentDeleteError"
    :segment-name="activeSegmentName"
    @delete="onDeleteSegment"
  />
</template>
